/**
 * Default English translations.
 *
 * Add additional locales by creating new files (e.g., es.ts, fr.ts)
 * and registering them with the i18n provider in config.ts.
 */

import type { UiTranslations } from '../types.js'

export const ui: UiTranslations = {
  // Common
  'common.loading': 'Loading...',
  'common.saving': 'Saving...',
  'common.close': 'Close',
  'common.goBack': 'Go back',
  'common.submit': 'Submit',
  'common.continue': 'Continue',

  // Auth - Login
  'auth.login.email': 'Email',
  'auth.login.password': 'Password',
  'auth.login.twoFactor': 'Two-Factor Token (If enabled)',
  'auth.login.signUp': 'Sign up',
  'auth.login.loggingIn': 'Logging in...',
  'auth.login.logIn': 'Log in',
  'auth.login.forgotPassword': 'Forgot password?',

  // Auth - Signup
  'auth.signup.email': 'Email (Required)',
  'auth.signup.password': 'Password (Required)',
  'auth.signup.name': 'Your name',
  'auth.signup.signingUp': 'Signing up...',
  'auth.signup.signUp': 'Sign up',

  // Auth - Forgot Password
  'auth.forgotPassword.success':
    'If an account with that email exists, a password reset link has been sent.',
  'auth.forgotPassword.email': 'Email',
  'auth.forgotPassword.submitting': 'Submitting...',

  // Auth - Reset Password
  'auth.resetPassword.email': 'Email',
  'auth.resetPassword.token': 'Password Reset Token',
  'auth.resetPassword.newPassword': 'Enter New Password',
  'auth.resetPassword.twoFactor': 'Two-Factor Token (If enabled)',
  'auth.resetPassword.loggingIn': 'Logging in...',
  'auth.resetPassword.submit': 'Set password & log in',

  // Home
  'home.greeting': 'Hello, ',
  'home.world': 'World',

  // Settings
  'settings.account': 'Account',
  'settings.email': 'Email',
  'settings.authentication': 'Authentication',
  'settings.changePassword': 'Change password',
  'settings.twoFactor': 'Two-factor authentication',
  'settings.notifications': 'Notifications',
  'settings.pushNotifications': 'Push notifications',
  'settings.billing': 'Billing',
  'settings.plan': 'Plan: ',
  'settings.upgrade': 'Upgrade',
  'settings.devices': 'Devices',
  'settings.noDevices': 'No devices found',
  'settings.thisDevice': 'This Device',
  'settings.platform': 'Platform',
  'settings.browser': 'Browser',
  'settings.network': 'Network',
  'settings.online': 'Online',
  'settings.offline': 'Offline',
  'settings.unknown': 'Unknown',
  'settings.logOut': 'Log out',
  'settings.deleteAccount': 'Delete account',
  'settings.changePasswordModal.title': 'Change Password',
  'settings.changePasswordModal.error': 'Failed to change password.',
  'settings.changePasswordModal.currentPassword': 'Current Password',
  'settings.changePasswordModal.newPassword': 'New Password',
  'settings.changePasswordModal.changing': 'Changing...',
  'settings.deleteAccountModal.title': 'Delete Account',
  'settings.deleteAccountModal.warning':
    'This permanently deletes your projects, deployed apps, databases and subscription. Teammates lose access to projects you shared with them. Enter your password to confirm.',
  'settings.deleteAccountModal.password': 'Password',
  'settings.deleteAccountModal.deleting': 'Deleting...',
  'settings.changePasswordModal.submit': 'Change password',
  'settings.deleteAccountModal.submit': 'Delete account',
  'settings.failedToUpdateEmail': 'Failed to update email.',
  'settings.failedToDeleteAccount': 'Could not delete your account. Try again in a minute.',
  'settings.toggleTwoFactor': 'Toggle two-factor authentication',
  'settings.togglePushNotifications': 'Toggle push notifications',

  // Footer
  'footer.about': 'About {{appName}}',
  'footer.privacyPolicy': 'Privacy Policy',
  'footer.termsOfService': 'Terms of Service',
  'footer.language': 'Language',

  // OAuth
  'oauth.orContinueWith': 'Or continue with',
  'oauth.continueWith': 'Continue with {{provider}}',
  'oauth.github': 'GitHub',
  'oauth.google': 'Google',
  'oauth.gitlab': 'GitLab',
  'oauth.twitter': 'Twitter',

  // Theme
  'theme.toggle': 'Toggle theme',

  // User Menu
  'userMenu.open': 'Open user menu',

  // Plan Updated
  'planUpdated.message': 'Your plan has been updated.',
  'planUpdated.thankYou': 'Thank you!',
  'planUpdated.returnHome': 'Return home',

  // PWA
  'pwa.updateAvailable': 'New version of the Molecule IDE is available!',
  'pwa.update': 'Update',
  'pwa.updating': 'Updating...',
  'version.summary.features_one': '{{count}} new feature',
  'version.summary.features_other': '{{count}} new features',
  'version.summary.fixes_one': '{{count}} bug fixed',
  'version.summary.fixes_other': '{{count}} bugs fixed',
  'version.summary.performance': 'performance improvements',
  'version.summary.minor': 'Minor improvements',
  'version.summary.featuresShort_one': '{{count}} feature',
  'version.summary.featuresShort_other': '{{count}} features',
  'version.summary.fixesShort_one': '{{count}} fix',
  'version.summary.fixesShort_other': '{{count}} fixes',
  'version.summary.performanceShort': 'faster',

  // User API errors
  'user.error.badRequest': 'Bad request.',
  'user.error.notFound': 'Not found.',
  'user.error.failedToCreateSession': 'Failed to create session.',
  'user.error.usernameRequired': 'Username is required.',
  'user.error.passwordRequired': 'Password is required.',
  'user.error.emailInvalid': 'Email is invalid.',
  'user.error.usernameUnavailable': 'Username is unavailable.',
  'user.error.emailAlreadyRegistered': 'Email is already registered.',
  'user.error.failedToHashPassword': 'Failed to hash password.',
  'user.error.invalidCredentials': 'Invalid credentials.',
  'user.error.invalidTwoFactorToken': 'Invalid two-factor token.',
  'user.error.twoFactorVerificationUnavailable': 'Two-factor verification unavailable.',
  'user.error.loginFailed': 'Login failed.',
  'user.error.usernameCannotBeEmpty': 'Username cannot be empty.',
  'user.error.failedToUpdateUser': 'Failed to update user.',
  'user.error.failedToDeleteUser': 'Failed to delete user.',
  'user.error.failedToReadUser': 'Failed to read user.',
  'user.error.emailRequired': 'Email is required.',
  'user.error.failedToProcessPasswordReset': 'Failed to process password reset.',
  'user.error.newPasswordRequired': 'New password is required.',
  'user.error.currentPasswordRequired': 'Current password is required.',
  'user.error.currentPasswordIncorrect': 'Current password is incorrect.',
  'user.error.failedToUpdatePassword': 'Failed to update password.',
  'user.error.planKeyRequired': 'planKey is required.',
  'user.error.invalidPlan': 'Invalid plan.',
  'user.error.failedToUpdateSubscription': 'Failed to update subscription.',
  'user.error.failedToUpdatePlan': 'Failed to update plan.',
  'user.error.twoFactorNotAvailable': 'Two-factor authentication is not available.',
  'user.error.tokenRequired': 'Token is required.',
  'user.error.noPendingTwoFactorSetup':
    'No pending two-factor setup. Call with action "setup" first.',
  'user.error.invalidToken': 'Invalid token.',
  'user.error.twoFactorNotEnabled': 'Two-factor is not enabled.',
  'user.error.invalidAction': 'Invalid action. Use "setup", "enable", or "disable".',
  'user.error.twoFactorOperationFailed': 'Two-factor operation failed.',
  'user.error.oauthServerNotConfigured': 'OAuth server "{{server}}" is not configured.',
  'user.error.oauthVerificationFailed': 'OAuth verification failed.',
  'user.error.failedToCreateUser': 'Failed to create user.',
  'user.error.oauthLoginFailed': 'OAuth login failed.',
  // Server errorKeys reachable from the auth/settings surfaces — English texts
  // mirror the server handlers' own defaults. useI18nError falls back to the
  // server's message for any key NOT listed here, so these exist to give
  // translators a worklist (and client-side re-translation on locale switch),
  // never to prevent a raw key from rendering — the hook guarantees that.
  'user.error.passwordTooShort': 'Password must be at least 8 characters',
  'user.error.passwordTooLong': 'Password must be at most 1024 characters',
  'user.error.tooManyRequests': 'Too many attempts. Please wait and try again.',
  'user.error.noUpdatableFields': 'No updatable fields were provided.',
  'user.error.avatarTooLarge': 'Avatar must be {{max}} characters or fewer.',
  'user.error.bioTooLong': 'Bio must be {{max}} characters or fewer.',
  'user.error.oauthDataInvalid': 'oauthData must be an object.',
  'user.error.oauthStateMismatch': 'OAuth state mismatch — possible CSRF attack',
  'user.error.twoFactorRequiresPassword':
    'Two-factor authentication is enabled on this account. Sign in with your email and password to continue.',
  'user.error.subscriptionActivationRequiresVerification':
    'Subscription activation must go through receipt verification — use POST /users/:id/verify-payment/:provider.',
  'user.payment.subscriptionOwnershipMismatch':
    'This subscription does not belong to your account.',
  'user.payment.transactionAlreadyClaimed':
    'This purchase is already associated with another account.',
  'onboarding.error.tokenRequired': 'A verification token is required.',
  'onboarding.error.invalidToken': 'This verification link is invalid or has already been used.',
  'onboarding.error.expiredToken': 'This verification link has expired. Please request a new one.',
  'onboarding.error.noEmail': 'No email address on file.',
  'onboarding.error.resendThrottled': 'A verification email was just sent — try again in a minute.',
  'signup.error.disposableEmail':
    'Please sign up with a permanent email address — disposable addresses are not allowed.',
  'auth.unauthorized': 'Unauthorized',

  // Auth client errors
  'auth.error.requestFailed': 'Request failed',
  'auth.error.loginFailed': 'Login failed',
  'auth.error.registrationFailed': 'Registration failed',
  'auth.error.noRefreshToken': 'No refresh token available',

  // Form validation
  'forms.required': 'This field is required',
  'forms.min': 'Value must be at least {{min}}',
  'forms.max': 'Value must be at most {{max}}',
  'forms.minLength': 'Must be at least {{minLength}} characters',
  'forms.maxLength': 'Must be at most {{maxLength}} characters',
  'forms.invalidFormat': 'Invalid format',
  'forms.invalidEmail': 'Invalid email address',
  'forms.invalidUrl': 'Invalid URL',
  'forms.invalidValue': 'Invalid value',

  // HTTP client errors
  'http.error.requestFailed': 'Request failed with status {{status}}.',
  'http.error.networkError': 'Network error.',

  // Routing errors
  'routing.error.missingParam': 'Missing param "{{name}}" for path "{{pattern}}"',
  'routing.error.routeNotFound': 'Route "{{name}}" not found',
  'routing.error.useMoleculeRouterOutsideProvider':
    'useMoleculeRouter must be used within a MoleculeRouterProvider',

  // Push notification errors
  'push.error.notSupported': 'Push notifications not supported',
  'push.error.permissionNotGranted': 'Notification permission not granted',

  // Utility errors
  'error.networkError': 'Network error. Please check your connection.',
  'error.timeout': 'Request timed out. Please try again.',
  'error.unauthorized': 'You are not authorized to perform this action.',
  'error.forbidden': 'Access denied.',
  'error.notFound': 'Resource not found.',
  'error.validationError': 'Please check your input and try again.',
  'error.serverError': 'Server error. Please try again later.',
  'error.unknown': 'An unexpected error occurred.',

  // AI conversation errors
  'conversation.error.messageRequired': 'message is required',
  'conversation.error.aiNotConfigured': 'AI provider not configured',
  'conversation.error.unknownAiError': 'Unknown AI error',
  'conversation.error.notFound': 'No conversation found',
  'conversation.error.streamError': 'AI streaming error',

  // Resource errors
  'resource.error.unknownError': 'Unknown error.',
  'resource.error.unableToCreate': 'Unable to create {{name}}.',
  'resource.error.unableToUpdate': 'Unable to update {{name}}.',
  'resource.error.unableToDelete': 'Unable to delete {{name}}.',
  'resource.error.notFound': 'Not found.',
  'resource.error.badRequest': 'Bad request.',
  'resource.error.unauthorized': 'Unauthorized.',

  // Project errors
  'project.error.nameAndTypeRequired': 'name and projectType are required',
  'project.error.notFound': 'Not found',

  // Device errors
  'device.error.unauthorized': 'Unauthorized.',
  'device.error.badRequest': 'Bad request.',
  'device.error.notFound': 'Not found.',

  // Code sandbox errors
  'codeSandbox.docker.error.readFailed': 'Failed to read {{path}}: {{error}}',
  'codeSandbox.docker.error.writeFailed': 'Failed to write {{path}}: {{error}}',
  'codeSandbox.docker.error.deleteFailed': 'Failed to delete {{path}}: {{error}}',
  'codeSandbox.docker.error.apiError': 'Docker API {{method}} {{path}}: {{status}} {{error}}',

  // Payment errors
  'user.payment.providerRequired': 'Payment provider is required.',
  'user.payment.subscriptionIdRequired': 'subscriptionId is required.',
  'user.payment.receiptAndPlanRequired': 'receipt and planKey are required.',
  'user.payment.verificationNotConfigured':
    'Payment verification is not configured for {{provider}}.',
  'user.payment.invalidPlan': 'Invalid plan for {{provider}}.',
  'user.payment.verificationFailed': 'Payment verification failed for {{provider}}.',
  'user.payment.unknownPlan': 'Unknown plan.',
  'user.payment.invalidWebhookEvent': 'Invalid webhook event.',

  // Common (additional)
  'common.cancel': 'Cancel',

  // Workspace
  'workspace.restart': 'Restart',
  'workspace.stop': 'Stop',
  'workspace.start': 'Start',
  'workspace.terminal': 'Terminal',
  'workspace.setup': 'Setup',
  'workspace.envVars': 'Env',
  'workspace.envVars.title': 'Environment Variables',
  'workspace.envVars.empty': 'No variables set.',
  'workspace.envVars.delete': 'Delete',
  'workspace.envVars.add': 'Add',
  'workspace.terminal.title': 'Terminal',
  'workspace.terminal.waiting': 'Waiting for sandbox...',
  'workspace.setup.title': 'Service Setup',
  'workspace.setup.steps': 'Setup Steps',
  'workspace.setup.docs': 'View documentation',
  'workspace.setup.save': 'Save',
  'workspace.contextMenu.deleteMultipleConfirm': 'Delete {{count}} items?',

  // Dashboard
  'dashboard.title': 'Your Projects',
  'dashboard.newProject': '+ New Project',
  'dashboard.newProject.placeholder': 'Describe your project (e.g. "SaaS with Stripe billing")…',
  'dashboard.newProject.creating': 'Creating…',
  'dashboard.newProject.create': 'Create',
  'dashboard.empty.title': 'No projects yet',
  'dashboard.empty.subtitle':
    'Tell Synthase what you want to build and it will create a full-stack app for you.',
  'dashboard.empty.cta': 'Create your first project',
  'dashboard.project.updated': 'Updated',
  'dashboard.project.open': 'Open',
  'dashboard.project.delete': 'Delete',
  'dashboard.project.stop': 'Stop',
  'dashboard.project.confirmDelete': 'Delete this project?',

  // Deploy
  'deploy.title': 'Deploy',
  'deploy.commitMessage': 'Commit message (optional)',
  'deploy.deploying': 'Deploying...',
  'deploy.sandboxRequired': 'Sandbox must be running to deploy.',
  'deploy.history': 'Deployment History',
  'deploy.noDeployments': 'No deployments yet.',
  'deploy.viewSite': 'View Site',
  'deploy.cancel': 'Cancel',
  'deploy.cicd': 'CI/CD Workflows',
  'deploy.cicdDesc':
    'Download a CI/CD workflow to automate builds and deployments from your repository.',
  'deploy.deployButton': 'Deploy',
  'deploy.target': 'Target',
  'deploy.targetMolecule': 'Molecule (hosted)',
  'deploy.ciProvider': 'Provider',
  'deploy.ciRecommended': 'Recommended for this app',
  'deploy.targetCloudflarePages': 'Cloudflare Pages',
  'deploy.targetNetlify': 'Netlify',
  'deploy.targetRender': 'Render',
  'deploy.targetFly': 'Fly.io',

  // Git push to the user's own repository (GIT1)
  'git.tab': 'Repository',
  'git.title': 'Push to your repository',
  'git.desc': 'Connect your own GitHub or GitLab account, then push the project to your repo.',
  'git.connect': 'Connect {{provider}}',
  'git.connected': 'Connected',
  'git.reconnect': 'Reconnect',
  'git.disconnect': 'Disconnect',
  'git.disconnected': 'Disconnected. Your saved credential was deleted.',
  'git.disconnectFailed': 'Could not disconnect. Please try again.',
  'git.scopes': 'Scopes: {{scopes}}',
  'git.unconfigured':
    'This server has no {{provider}} app configured. Ask the operator to set up a git provider app.',
  'git.connectedNotice': 'Connected to {{provider}}.',
  'git.connectError': 'Could not connect to {{provider}}. Please try again.',
  'git.sshTitle': 'SSH key',
  'git.sshDesc': 'Or paste a PEM private key to push over SSH.',
  'git.sshPlaceholder': '-----BEGIN OPENSSH PRIVATE KEY-----',
  'git.sshSave': 'Save SSH key',
  'git.sshSaved': 'SSH key saved.',
  'git.sshFingerprint': 'Fingerprint: {{fingerprint}}',
  'git.sshInvalid': 'Could not parse that SSH key. Paste a valid PEM private key.',
  'git.origin': 'Repository origin URL',
  'git.originPlaceholder': 'https://github.com/you/your-repo.git',
  'git.push': 'Push',
  'git.pushing': 'Pushing...',
  'git.pushed': 'Pushed successfully.',
  'git.pushFailed': 'Push failed.',
  'git.originMismatch':
    'That origin host is not one of your connected GitHub/GitLab providers. Push only to your own repository, or use an SSH origin.',
  'git.sandboxRequired': 'Sandbox must be running to push.',
  'git.loadError': 'Failed to load git connection status.',
  'git.pushRepo': 'Repository to push',
  'git.pull': 'Pull',
  'git.pulling': 'Pulling...',
  'git.pulled': 'Pulled successfully.',
  'git.pullFailed': 'Pull failed.',
  'git.pullConflict':
    'Pull hit merge conflicts, so it was aborted and your work was left untouched. Synthase can help you resolve the conflicting files ({{files}}).',
  'git.pullDiverged':
    'Pull could not fast-forward — your sandbox and the remote have both changed. Synthase can reconcile them with a merge or rebase.',
  'git.pullDirtyTree':
    'Pull blocked — you have uncommitted changes that would be overwritten. Commit or discard them, then pull again.',

  // Existing-repo import (GIT2)
  'chat.importRepo.text': 'Connect your repository so Synthase can work on your actual code.',
  'chat.importRepo.choose': 'Choose repository',
  'chat.importRepo.skip': 'Skip',
  'importRepo.title': 'Import your repository',
  'importRepo.subtitle':
    'Your code is cloned into a private workspace on its own branch — your existing branches are never touched.',
  'importRepo.connect': 'Connect {{provider}}',
  'importRepo.connectUnavailable':
    'Private repositories can’t be imported here — this server has no GitHub/GitLab app configured.',
  'importRepo.private': 'private',
  'importRepo.add': 'Add',
  'importRepo.importOne': 'Import 1 repository',
  'importRepo.importMany': 'Import {{count}} repositories',
  'importRepo.importAtLeastOne': 'Import at least 1 repository',
  'importRepo.more': 'Load more',
  'importRepo.loading': 'Loading repositories…',
  'importRepo.publicUrl': 'Paste a public repository URL',
  'importRepo.branchPlaceholder': 'branch (optional)',
  'importRepo.or': 'or',
  'importRepo.resumeMessage':
    "I've selected my repository to import: {{label}} (branch: {{branch}}). Please continue.",
  'importRepo.skipMessage': "Skip the import — let's work here without importing my code.",
  'importRepo.uploadLabel': 'Upload a .tar.gz of your codebase',
  'importRepo.uploadButton': 'Choose file…',
  'importRepo.uploading': 'Uploading…',
  'importRepo.error.uploadUnavailable':
    'File upload is not available here — connect a repository instead.',
  'importRepo.error.listFailed': 'Could not load your repositories. Try again.',
  'importRepo.error.saveFailed': 'Could not select that repository. Try again.',

  // IDE
  'ide.chat.title': 'Chat',
  'ide.chat.emptyState': 'Describe what you want to build...',
  'ide.chat.you': 'You',
  'ide.chat.molecule': 'Molecule',
  'ide.chat.placeholder': 'Type a message...',
  'ide.chat.stop': 'Stop',
  'ide.chat.send': 'Send',
  'ide.chat.commit': 'Commit',
  'ide.chat.committing': 'Committing...',
  'ide.chat.committed': 'Committed',
  'ide.chat.commitFailed': 'Commit failed',

  // Guest reminder
  'guest.reminder.message':
    'Sign up or log in to keep your work. Guest sessions expire after 72 hours.',
  'guest.reminder.signUp': 'Sign up',
  'guest.reminder.logIn': 'Log in',
  'guest.reminder.dismiss': 'Dismiss',
  'guest.firstBuild.message': 'Your app is running. Sign up to save it and deploy it live.',

  // Upgrade prompts
  'upgrade.signUp': 'Sign up',
  'upgrade.viewPlans': 'View plans',
  'upgrade.aiLimit': 'Daily AI message limit reached. Upgrade for more.',
  'upgrade.projectLimit': 'Project limit reached. Upgrade for more.',
  'upgrade.sandboxLimit': 'Concurrent sandbox limit reached. Upgrade for more.',
  'upgrade.deployLimit': 'Active deployment limit reached. Upgrade for more.',
  'ide.editor.title': 'Editor',
  'ide.editor.emptyState': 'Select a file to start editing',
  'ide.preview.title': 'Preview',
  'ide.preview.refresh': 'Refresh',
  'ide.preview.openNewTab': 'Open in new tab',
  'ide.preview.livePreview': 'Live Preview',
  'ide.preview.starting': 'Loading preview...',
  'ide.preview.noPreview': 'No preview available',
  'ide.tabs.close': 'Close {{fileName}}',
  'ide.files.empty': 'No files',
  'ide.device.responsive': 'Responsive',
  'ide.device.desktop': 'Desktop',
  'ide.device.tablet': 'Tablet',
  'ide.device.mobile': 'Mobile',
  'ide.toolCall.running': 'Running...',
  'ide.toolCall.input': 'Input',
  'ide.toolCall.output': 'Output',

  // Domain
  'domain.title': 'Custom Domain',
  'domain.saving': 'Saving...',
  'domain.save': 'Set Domain',
  'domain.dnsInstructions': 'Add these DNS records at your domain registrar:',
  'domain.verifying': 'Verifying...',
  'domain.verify': 'Verify DNS',

  // Personal API tokens (settings panel)
  'settings.apiTokens.heading': 'API tokens',
  'settings.apiTokens.summary': 'Tokens for scripts, CI and the CLI',
  'settings.nav.apiTokens': 'API tokens',
  'apiTokens.intro':
    'For scripts, CI and the CLI. A token acts as your account, except for security actions such as changing your password or email, managing tokens or deleting the account.',
  'apiTokens.docsLink': 'How tokens work',
  'apiTokens.loadFailed': 'Could not load your tokens.',
  'apiTokens.empty': 'No tokens yet.',
  'apiTokens.created': 'Created {{date}}',
  'apiTokens.expires': 'Expires {{date}}',
  'apiTokens.expired': 'Expired {{date}}',
  'apiTokens.lastUsed': 'Last used {{date}}',
  'apiTokens.neverUsed': 'Never used',
  'apiTokens.revoke': 'Revoke',
  'apiTokens.revokeConfirm': 'Revoke "{{name}}"? Anything using it will stop working.',
  'apiTokens.revokeFailed': 'Could not revoke that token.',
  'apiTokens.nameLabel': 'Token name',
  'apiTokens.namePlaceholder': 'GitHub Actions',
  'apiTokens.expiryLabel': 'Expires in',
  'apiTokens.expiryDays': '{{days}} days',
  'apiTokens.create': 'Create token',
  'apiTokens.createFailed': 'Could not create the token.',
  'apiTokens.limitReached': 'You have {{max}} active tokens. Revoke one to create another.',
  'apiTokens.sessionRequired': 'Managing tokens needs a signed-in session, not an API token.',
  'apiTokens.emailNotConfirmed': 'Confirm your email address first, then create a token.',
  'apiTokens.reveal.label': 'Your new token: {{name}}',
  'apiTokens.reveal.warning': 'Copy it now. It is shown only once.',
  'apiTokens.reveal.copy': 'Copy token',
  'apiTokens.reveal.copied': 'Token copied',
  'apiTokens.reveal.copyFailed': 'Could not copy. Select the token and copy it yourself.',
  'apiTokens.reveal.dismiss': 'Hide token',

  // Template pages — try the template (quick preview / live app)
  'templatePage.tryIt': 'Try it',
  'templatePage.liveDemo': 'Live demo',
  'templatePage.modeQuick': 'Quick preview',
  'templatePage.modeLive': 'Live app',
  'templatePage.modeQuickHint': 'Opens instantly. Runs on sample data, so changes reset on reload.',
  'templatePage.modeLiveHint': 'The real deployed app. It can take a few seconds to start.',
  'templatePage.playPreview': 'Open the quick preview',
  'templatePage.playPreviewShort': 'Open quick preview',
  'templatePage.playDemo': 'Play the live demo',
  'templatePage.playDemoShort': 'Run the live app',
  'templatePage.previewFrameTitle': '{{name}} — preview with sample data',
  'templatePage.demoFrameTitle': '{{name}} — live demo',
  'templatePage.previewLoading': 'Loading the preview…',
  'templatePage.demoWaking': 'Starting the demo…',
  'templatePage.openPreviewFull': 'Open preview in a new tab ↗',
  'templatePage.openFull': 'Open full version ↗',
  'templatePage.viewWorkspace': 'View workspace ↗',

  // Static template previews — the ribbon inside the preview
  'staticPreview.ribbon.label': 'Preview with sample data',
  'staticPreview.ribbon.cta': 'Start your own',

  // Sandbox start — why it is waiting, and how long is left (BootWaitNotice)
  'workspace.bootWait.queue': 'Waiting for an available sandbox. You are next in line.',
  'workspace.bootWait.queuePosition':
    'Waiting for an available sandbox. You are number {{position}} in line.',
  'workspace.bootWait.serverRoom':
    'Waiting for room on the server while other sandboxes start or go to sleep.',
  'workspace.bootWait.clearingSpace':
    'The platform is freeing up space on the server. Your sandbox starts as soon as there is room.',
  'workspace.bootWait.cancel': 'Cancel',
  'workspace.bootWait.cancelFailed': 'Could not cancel. The sandbox may already be starting.',
  'workspace.bootWait.restoringBackup': 'Restoring your project from its backup.',
  'workspace.bootWait.restoring': 'Restoring your project from its archive.',
  'workspace.bootWait.coldBoot':
    'Setting this project up from scratch. The next start will be faster.',
  'workspace.bootWait.remaining': 'About {{time}} left',
  'workspace.bootWait.overrun': 'Taking longer than usual · {{time}} so far',
  'workspace.bootWait.elapsed': '{{time}} so far',
}
